// src/Components/AdvDetail/index.tsx

import React, { useState, useCallback, useMemo, memo, useEffect } from "react";
import type { P2PAdDetail } from "../../exchanges/domain";
import { useExchangeAdapter } from "../../contexts/ExchangeContext";
import {
  FaSearch,
  FaUserCircle,
  FaClock,
  FaShieldAlt,
  FaPercent,
  FaHourglassHalf,
  FaChartLine,
  FaInfoCircle,
  FaExclamationTriangle,
  FaCopy,
  FaHeart,
  FaHistory,
  FaTimes,
} from "react-icons/fa";

interface AdvDetailProps {
  initialAdvNo?: string;
  onAdvDataChange?: (data: P2PAdDetail | null) => void;
}

const SkeletonLoader: React.FC = memo(() => (
  <div className="bg-gray-800 rounded-lg shadow-xl p-3 sm:p-6 space-y-4 sm:space-y-6 animate-pulse">
    {/* Header Skeleton */}
    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center border-b border-gray-700 pb-4 space-y-3 sm:space-y-0">
      <div className="h-6 sm:h-8 bg-gray-700 rounded w-48"></div>
      <div className="flex gap-2">
        <div className="h-6 bg-gray-700 rounded w-16"></div>
        <div className="h-6 bg-gray-700 rounded w-20"></div>
      </div>
    </div>
    
    {/* Main Info Grid Skeleton */}
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
      {[1, 2, 3].map((i) => (
        <div key={i} className="p-3 sm:p-4 bg-gray-700/30 rounded-lg border border-gray-700/50">
          <div className="h-4 bg-gray-700 rounded w-16 mb-2"></div>
          <div className="h-6 sm:h-8 bg-gray-700 rounded w-32 mb-2"></div>
          <div className="h-3 bg-gray-700 rounded w-24"></div>
        </div>
      ))}
    </div>
    
    {/* Merchant Info Skeleton */}
    <div className="bg-gray-700/30 p-3 sm:p-4 rounded-lg border border-gray-700/50">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-8 h-8 bg-gray-700 rounded-full"></div>
        <div className="space-y-2">
          <div className="h-4 bg-gray-700 rounded w-32"></div>
          <div className="h-3 bg-gray-700 rounded w-24"></div>
        </div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="p-3 bg-gray-800/50 rounded-lg">
            <div className="h-3 bg-gray-700 rounded w-16 mb-2"></div>
            <div className="h-4 bg-gray-700 rounded w-12"></div>
          </div>
        ))}
      </div>
    </div>
    
    {/* Payment Methods Skeleton */}
    <div>
      <div className="h-4 bg-gray-700 rounded w-40 mb-3"></div>
      <div className="flex flex-wrap gap-2">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-8 bg-gray-700 rounded w-20"></div>
        ))}
      </div>
    </div>
  </div>
));

const AdvDetail: React.FC<AdvDetailProps> = ({ 
  initialAdvNo, 
  onAdvDataChange 
}) => {
  const levelAccess = useMemo(() => {
    const url = window.location.href;
    if (url.includes("admin")) return 2;
    if (url.includes("premium")) return 1;
    return 0;
  }, []);

  const adapter = useExchangeAdapter();

  const [advNo, setAdvNo] = useState<string>("");
  const [advData, setAdvData] = useState<P2PAdDetail | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<{ type: 'validation' | 'network' | 'api' | 'unknown'; message: string; details?: string } | null>(null);
  const [searchHistory, setSearchHistory] = useState<{advNo: string, nickname: string, tradeType: string}[]>([]);
  const [favorites, setFavorites] = useState<string[]>([]);
  const [showHistory, setShowHistory] = useState<boolean>(false);

  const formatMoney = useCallback((amount: string | number): string => {
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    return !isNaN(num) ? num.toLocaleString("vi-VN") : "0";
  }, []);

  const formatPercent = useCallback((value: string | number): string => {
    const num = typeof value === 'string' ? parseFloat(value) : value;
    return !isNaN(num) ? (num * 100).toFixed(5) + "%" : "0.00000%";
  }, []);

  const formatDate = useCallback((timestamp: number): string => {
    if (!timestamp || timestamp <= 0) return "N/A";
    try {
      const date = new Date(timestamp);
      const options: Intl.DateTimeFormatOptions = {
        timeZone: "Asia/Ho_Chi_Minh",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      };
      return date.toLocaleString("vi-VN", options);
    } catch {
      return "Invalid date";
    }
  }, []);

  const validateAdvNo = useCallback((advNo: string): boolean => {
    const trimmed = advNo.trim();
    return trimmed.length >= 8 && trimmed.length <= 50 && /^[a-zA-Z0-9]+$/.test(trimmed);
  }, []);

  const sanitizeText = useCallback((text: string): string => {
    return text.replace(/[<>"'&]/g, (char) => {
      switch (char) {
        case '<': return '&lt;';
        case '>': return '&gt;';
        case '"': return '&quot;';
        case "'": return '&#x27;';
        case '&': return '&amp;';
        default: return char;
      }
    });
  }, []);


  const processedAdvData = useMemo(() => {
    if (!advData) return null;
    
    return {
      ...advData,
      formattedPrice: formatMoney(advData.adv?.price || "0"),
      formattedSurplus: Number(advData.adv?.surplusAmount || 0).toFixed(
        Math.min(advData.adv?.assetScale || 8, 4)
      ),
      formattedTradable: Number(advData.adv?.tradableQuantity || 0).toFixed(
        Math.min(advData.adv?.assetScale || 8, 4)
      ),
      formattedMinAmount: formatMoney(advData.adv?.minSingleTransAmount || "0"),
      formattedMaxAmount: formatMoney(advData.adv?.maxSingleTransAmount || "0"),
      formattedDynamicMax: formatMoney(advData.adv?.dynamicMaxSingleTransAmount || "0"),
      formattedCommission: formatPercent(advData.adv?.commissionRate || "0"),
      formattedMonthRate: formatPercent((advData.advertiser?.monthFinishRate || 0).toString()),
      formattedOrderCount: (advData.advertiser?.orderCount || 0).toLocaleString(),
      formattedCreateTime: formatDate(advData.adv?.createTime || 0),
      formattedUpdateTime: formatDate(advData.adv?.advUpdateTime || 0),
    };
  }, [advData, formatMoney, formatPercent, formatDate]);

  // Load search history and favorites from localStorage, set default AdvNo
  useEffect(() => {
    try {
      const savedHistory = localStorage.getItem('advDetail_searchHistory');
      const savedFavorites = localStorage.getItem('advDetail_favorites');
      
      if (savedHistory) {
        const history = JSON.parse(savedHistory);
        if (Array.isArray(history)) {
          // Handle both old format (string) and new format (object)
          const formattedHistory = history.map(item => {
            if (typeof item === 'string') {
              return { advNo: item, nickname: '', tradeType: '' };
            }
            return item;
          });
          const limitedHistory = formattedHistory.slice(0, 10); // Limit to 10 items
          setSearchHistory(limitedHistory);
          
          // Set default AdvNo: prioritize initialAdvNo, then last search history
          if (initialAdvNo) {
            setAdvNo(initialAdvNo);
          } else if (limitedHistory.length > 0) {
            setAdvNo(limitedHistory[0].advNo); // Use the most recent search
          }
        } else if (initialAdvNo) {
          setAdvNo(initialAdvNo);
        }
      } else if (initialAdvNo) {
        setAdvNo(initialAdvNo);
      }
      
      if (savedFavorites) {
        const favs = JSON.parse(savedFavorites);
        if (Array.isArray(favs)) {
          setFavorites(favs.slice(0, 20)); // Limit to 20 items
        }
      }
    } catch (error) {
      console.warn('Failed to load search history/favorites:', error);
      if (initialAdvNo) {
        setAdvNo(initialAdvNo);
      }
    }
  }, [initialAdvNo]);

  // Save to localStorage whenever history or favorites change
  useEffect(() => {
    try {
      localStorage.setItem('advDetail_searchHistory', JSON.stringify(searchHistory));
    } catch (error) {
      console.warn('Failed to save search history:', error);
    }
  }, [searchHistory]);

  useEffect(() => {
    try {
      localStorage.setItem('advDetail_favorites', JSON.stringify(favorites));
    } catch (error) {
      console.warn('Failed to save favorites:', error);
    }
  }, [favorites]);

  const addToSearchHistory = useCallback((advNo: string, nickname?: string, tradeType?: string) => {
    if (!advNo || !validateAdvNo(advNo)) return;
    
    const historyItem = {
      advNo,
      nickname: nickname || '',
      tradeType: tradeType || ''
    };
    
    setSearchHistory(prev => {
      const filtered = prev.filter(item => item.advNo !== advNo);
      return [historyItem, ...filtered].slice(0, 10);
    });
  }, [validateAdvNo]);

  const toggleFavorite = useCallback((advNo: string) => {
    if (!advNo) return;
    
    setFavorites(prev => {
      const isFavorite = prev.includes(advNo);
      if (isFavorite) {
        return prev.filter(item => item !== advNo);
      } else {
        return [advNo, ...prev].slice(0, 20);
      }
    });
  }, []);

  const clearHistory = useCallback(() => {
    setSearchHistory([]);
  }, []);

  const deleteFromHistory = useCallback((advNoToDelete: string) => {
    setSearchHistory(prev => prev.filter(item => item.advNo !== advNoToDelete));
  }, []);

  const fetchAdvDetail = useCallback(async () => {
    const trimmedAdvNo = advNo.trim();

    if (!trimmedAdvNo) {
      setError({
        type: 'validation',
        message: "Vui lòng nhập mã AdvNo"
      });
      return;
    }

    if (!validateAdvNo(trimmedAdvNo)) {
      setError({
        type: 'validation',
        message: "Mã AdvNo không hợp lệ",
        details: "AdvNo phải có độ dài từ 8-50 ký tự và chỉ chứa chữ cái, số"
      });
      return;
    }

    setIsLoading(true);
    setError(null);
    setAdvData(null);

    try {
      const data = await adapter.getAdDetail(trimmedAdvNo);

      if (!data) {
        setError({
          type: 'api',
          message: "Không tìm thấy thông tin quảng cáo"
        });
        setIsLoading(false);
        return;
      }

      // Sanitize text fields (security)
      const sanitizedData: P2PAdDetail = {
        ...data,
        adv: {
          ...data.adv,
          remarks: sanitizeText(data.adv.remarks),
          autoReplyMsg: data.adv.autoReplyMsg ? sanitizeText(data.adv.autoReplyMsg) : null,
        },
        advertiser: {
          ...data.advertiser,
          nickName: sanitizeText(data.advertiser.nickName),
          badges: data.advertiser.badges?.map((badge: string) => sanitizeText(badge)) || [],
        }
      };

      setAdvData(sanitizedData);
      onAdvDataChange?.(sanitizedData);
      addToSearchHistory(trimmedAdvNo, sanitizedData.advertiser.nickName, sanitizedData.adv.tradeType);
    } catch (err) {
      const error = err as Error;
      let errorType: 'validation' | 'network' | 'api' | 'unknown' = 'unknown';
      let errorMessage = "Có lỗi xảy ra khi tải dữ liệu";

      if (error.message.includes('timeout') || error.name === 'AbortError') {
        errorType = 'network';
        errorMessage = "Kết nối quá chậm, vui lòng thử lại";
      } else if (error.name === 'HttpError' || (err as any)?.response?.status) {
        errorType = 'api';
        errorMessage = `Lỗi API: ${(err as any)?.response?.status ?? 'unknown'}`;
      }

      setError({
        type: errorType,
        message: errorMessage
      });
      console.error("Error fetching data:", err);
    } finally {
      setIsLoading(false);
    }
  }, [advNo, validateAdvNo, onAdvDataChange, sanitizeText, addToSearchHistory, adapter]);

  if (levelAccess < 1) {
    return <></>;
  }

  return (
    <main className="min-h-screen bg-gray-900 px-2 py-4 sm:px-4 lg:px-6" role="main">
      <div className="max-w-6xl mx-auto">
        {/* Search Section */}
        <div className="mb-4 sm:mb-6">
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
            <div className="relative flex-1">
              <input
                type="text"
                value={advNo}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAdvNo(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && !isLoading && fetchAdvDetail()}
                onFocus={() => setShowHistory(true)}
                onBlur={() => {
                  // Hide history when clicking outside, but not when clicking on history items
                  setTimeout(() => setShowHistory(false), 150);
                }}
                placeholder="Nhập mã AdvNo..."
                className="w-full px-4 py-3 pr-20 bg-gray-800 border border-gray-700 rounded-lg text-gray-100 placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all duration-200"
                aria-label="Mã số quảng cáo"
                aria-describedby={error ? 'error-message' : undefined}
                aria-invalid={error?.type === 'validation'}
                autoComplete="off"
                spellCheck="false"
              />
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center gap-2">
                {searchHistory.length > 0 && (
                  <button
                    onClick={() => setShowHistory(!showHistory)}
                    className="p-1 text-gray-500 hover:text-gray-300 transition-colors"
                    title="Lịch sử tìm kiếm"
                    aria-label="Hiện/ẩn lịch sử tìm kiếm"
                  >
                    <FaHistory className="w-4 h-4" />
                  </button>
                )}
                <FaSearch className="text-gray-500 pointer-events-none" />
              </div>
              
              {/* Search History Dropdown */}
              {showHistory && (searchHistory.length > 0 || favorites.length > 0) && (
                <div className="absolute z-10 w-full mt-1 bg-gray-800 border border-gray-700 rounded-lg shadow-xl max-h-64 overflow-y-auto">
                  {favorites.length > 0 && (
                    <div>
                      <div className="px-3 py-2 text-xs font-medium text-gray-400 border-b border-gray-700">
                        Yêu thích
                      </div>
                      {favorites.map((fav, index) => (
                        <button
                          key={`fav-${index}`}
                          onClick={() => {
                            setAdvNo(fav);
                            setShowHistory(false);
                          }}
                          className="w-full px-3 py-2 text-left text-gray-300 hover:bg-gray-700/50 transition-colors flex items-center justify-between group"
                        >
                          <span className="truncate">{fav}</span>
                          <FaHeart className="w-3 h-3 text-red-400 opacity-60" />
                        </button>
                      ))}
                    </div>
                  )}
                  
                  {searchHistory.length > 0 && (
                    <div>
                      <div className="px-3 py-2 text-xs font-medium text-gray-400 border-b border-gray-700 flex items-center justify-between">
                        <span>Lịch sử</span>
                        <button
                          onClick={clearHistory}
                          className="text-red-400 hover:text-red-300 text-xs"
                        >
                          Xóa tất cả
                        </button>
                      </div>
                      {searchHistory.map((item, index) => (
                        <div
                          key={`history-${index}`}
                          className="flex items-center group hover:bg-gray-700/50 transition-colors"
                        >
                          <button
                            onClick={() => {
                              setAdvNo(item.advNo);
                              setShowHistory(false);
                            }}
                            className="flex-1 px-3 py-2 text-left text-gray-300"
                          >
                            <div className="truncate">
                              <div className="font-medium">{item.advNo}</div>
                              {(item.nickname || item.tradeType) && (
                                <div className="text-xs text-gray-400 mt-1">
                                  {item.nickname && <span>{item.nickname}</span>}
                                  {item.nickname && item.tradeType && <span> • </span>}
                                  {item.tradeType && (
                                    <span className={`px-1 py-0.5 rounded text-xs ${
                                      item.tradeType === 'SELL' 
                                        ? 'bg-red-900/50 text-red-200' 
                                        : 'bg-green-900/50 text-green-200'
                                    }`}>
                                      {item.tradeType}
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteFromHistory(item.advNo);
                            }}
                            className="px-2 py-2 text-gray-500 hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100"
                            title={`Xóa ${item.advNo}`}
                            aria-label={`Xóa ${item.advNo} khỏi lịch sử`}
                          >
                            <FaTimes className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            <button
              onClick={fetchAdvDetail}
              disabled={isLoading || !advNo.trim()}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-medium min-w-[120px] sm:min-w-0"
              aria-label="Search advertisement"
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  Đang tải...
                </div>
              ) : (
                "Kiểm tra"
              )}
            </button>
          </div>
        </div>

        {error && (
          <div 
            id="error-message"
            role="alert"
            aria-live="polite"
            className={`p-4 mb-6 border rounded-lg flex items-start space-x-3 ${
              error.type === 'validation' 
                ? 'bg-yellow-900/50 border-yellow-700 text-yellow-200'
                : error.type === 'network'
                ? 'bg-orange-900/50 border-orange-700 text-orange-200' 
                : 'bg-red-900/50 border-red-700 text-red-200'
            }`}
          >
            <FaExclamationTriangle className="mt-0.5 flex-shrink-0" aria-hidden="true" />
            <div className="flex-1">
              <div className="font-medium">{error.message}</div>
              {error.details && (
                <div className="text-sm opacity-80 mt-1">{error.details}</div>
              )}
            </div>
          </div>
        )}

        {isLoading && !advData && (
          <div role="status" aria-label="Đang tải dữ liệu quảng cáo">
            <SkeletonLoader />
            <span className="sr-only">Đang tải dữ liệu quảng cáo, vui lòng chờ...</span>
          </div>
        )}
        
        {advData && (
          <article className="bg-gray-800 rounded-lg shadow-xl p-3 sm:p-6 space-y-4 sm:space-y-6" aria-label="Chi tiết quảng cáo">
            {/* Header */}
            <header className="flex flex-col sm:flex-row sm:justify-between sm:items-center border-b border-gray-700 pb-4 space-y-3 sm:space-y-0">
              <div className="flex items-center justify-between sm:justify-start">
                <h2 className="text-lg sm:text-xl font-bold text-white">
                  {advData.adv?.tradeType} {advData.adv?.asset}/{advData.adv?.fiatUnit}
                </h2>
                <div className="flex gap-2 sm:hidden">
                  <button 
                    onClick={() => navigator.clipboard?.writeText(advData.adv?.advNo || '')}
                    className="p-2 text-gray-400 hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/20 rounded"
                    title="Sao chép mã quảng cáo"
                    aria-label="Sao chép mã quảng cáo"
                  >
                    <FaCopy className="w-4 h-4" aria-hidden="true" />
                  </button>
                  <button 
                    onClick={() => toggleFavorite(advData.adv?.advNo || '')}
                    className={`p-2 transition-colors focus:outline-none focus:ring-2 focus:ring-red-500/20 rounded ${
                      favorites.includes(advData.adv?.advNo || '') 
                        ? 'text-red-400 hover:text-red-300' 
                        : 'text-gray-400 hover:text-red-400'
                    }`}
                    title={favorites.includes(advData.adv?.advNo || '') ? 'Xóa khỏi yêu thích' : 'Thêm vào yêu thích'}
                    aria-label={favorites.includes(advData.adv?.advNo || '') ? 'Xóa khỏi yêu thích' : 'Thêm vào yêu thích'}
                  >
                    <FaHeart className={`w-4 h-4 ${favorites.includes(advData.adv?.advNo || '') ? 'fill-current' : ''}`} aria-hidden="true" />
                  </button>
                </div>
              </div>
              <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                <span
                  className={`px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium ${
                    advData.adv?.tradeType === "SELL"
                      ? "bg-red-900/50 text-red-200 border border-red-700/50"
                      : "bg-green-900/50 text-green-200 border border-green-700/50"
                  }`}
                >
                  {advData.adv?.tradeType}
                </span>
                <span className="bg-blue-900/50 text-blue-200 border border-blue-700/50 px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium">
                  {advData.adv?.classify}
                </span>
                <div className="hidden sm:flex gap-2">
                  <button 
                    onClick={() => navigator.clipboard?.writeText(advData.adv?.advNo || '')}
                    className="p-2 text-gray-400 hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/20 rounded"
                    title="Sao chép mã quảng cáo"
                    aria-label="Sao chép mã quảng cáo"
                  >
                    <FaCopy className="w-4 h-4" aria-hidden="true" />
                  </button>
                  <button 
                    onClick={() => toggleFavorite(advData.adv?.advNo || '')}
                    className={`p-2 transition-colors focus:outline-none focus:ring-2 focus:ring-red-500/20 rounded ${
                      favorites.includes(advData.adv?.advNo || '') 
                        ? 'text-red-400 hover:text-red-300' 
                        : 'text-gray-400 hover:text-red-400'
                    }`}
                    title={favorites.includes(advData.adv?.advNo || '') ? 'Xóa khỏi yêu thích' : 'Thêm vào yêu thích'}
                    aria-label={favorites.includes(advData.adv?.advNo || '') ? 'Xóa khỏi yêu thích' : 'Thêm vào yêu thích'}
                  >
                    <FaHeart className={`w-4 h-4 ${favorites.includes(advData.adv?.advNo || '') ? 'fill-current' : ''}`} aria-hidden="true" />
                  </button>
                </div>
              </div>
            </header>

            {/* Main Info Grid */}
            <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4" aria-label="Thông tin chính">
              {/* Price Info */}
              <div className="p-3 sm:p-4 bg-gray-700/30 rounded-lg border border-gray-700/50 hover:bg-gray-700/50 transition-colors" role="region" aria-label="Thông tin giá">
                <div className="text-gray-400 mb-1 text-sm font-medium">Giá</div>
                <div className="text-xl sm:text-2xl font-bold text-green-400 break-words">
                  {processedAdvData?.formattedPrice}
                  <span className="text-base sm:text-lg ml-1">{advData.adv?.fiatSymbol}</span>
                </div>
                <div className="text-xs sm:text-sm text-gray-400 mt-2">
                  Phí: {processedAdvData?.formattedCommission}
                </div>
              </div>

              {/* Amount Info */}
              <div className="p-3 sm:p-4 bg-gray-700/30 rounded-lg border border-gray-700/50 hover:bg-gray-700/50 transition-colors">
                <div className="text-gray-400 mb-1 text-sm font-medium">Khả dụng</div>
                <div className="text-xl sm:text-2xl font-bold text-blue-400 break-words">
                  {processedAdvData?.formattedSurplus}
                  <span className="text-base sm:text-lg ml-1">{advData.adv?.asset}</span>
                </div>
                <div className="text-xs sm:text-sm text-gray-400 mt-2 space-y-1">
                  <div>Số lượng khởi tạo: {formatMoney(advData.adv?.initAmount || "0")} {advData.adv?.asset}</div>
                  <div>Đã giao dịch trước đó: {formatMoney(String((advData.adv?.initAmount || 0) - (advData.adv?.surplusAmount || 0)))} {advData.adv?.asset}</div>
                  <div>Hiện tại đang chờ: {processedAdvData?.formattedTradable} {advData.adv?.asset}</div>
                  <div className="pt-1 border-t border-gray-600 mt-2">
                    <strong>Tạo lúc:</strong> {processedAdvData?.formattedCreateTime}
                  </div>
                </div>
              </div>

              {/* Limits Info */}
              <div className="p-3 sm:p-4 bg-gray-700/30 rounded-lg border border-gray-700/50 hover:bg-gray-700/50 transition-colors sm:col-span-2 lg:col-span-1">
                <div className="text-gray-400 mb-1 text-sm font-medium">Giới hạn giao dịch</div>
                <div className="text-base sm:text-lg font-bold text-yellow-400 break-words">
                  {processedAdvData?.formattedMinAmount} - {processedAdvData?.formattedMaxAmount}
                  <span className="text-sm ml-1">{advData.adv?.fiatUnit}</span>
                </div>
                <div className="text-xs sm:text-sm text-gray-400 mt-2">
                  Tối đa: {processedAdvData?.formattedDynamicMax}
                </div>
              </div>
            </section>

            {/* Merchant Info */}
            <section className="bg-gray-700/30 p-3 sm:p-4 rounded-lg border border-gray-700/50" aria-label="Thông tin người bán">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 space-y-3 sm:space-y-0">
                <div className="flex items-center gap-3">
                  {advData.advertiser?.avatar ? (
                    <img 
                      src={advData.advertiser.avatar} 
                      alt={advData.advertiser.nickName}
                      className="w-8 h-8 sm:w-10 sm:h-10 rounded-full border border-gray-600"
                      onError={(e) => { 
                        (e.target as HTMLImageElement).style.display = 'none';
                        (e.target as HTMLImageElement).nextElementSibling?.classList.remove('hidden');
                      }}
                    />
                  ) : null}
                  <FaUserCircle className={`text-xl sm:text-2xl text-gray-400 flex-shrink-0 ${advData.advertiser?.avatar ? 'hidden' : ''}`} />
                  <div className="min-w-0 flex-1">
                    <div className="font-bold text-white text-sm sm:text-base truncate">
                      {advData.advertiser?.nickName}
                      {advData.advertiser?.isCompanyAccount && (
                        <span className="ml-2 px-1 py-0.5 bg-blue-900/50 text-blue-200 text-xs rounded">
                          Công ty
                        </span>
                      )}
                    </div>
                    <div className="text-xs sm:text-sm text-gray-400">
                      {advData.advertiser?.userIdentity} • VIP {advData.advertiser?.vipLevel} • Grade {advData.advertiser?.userGrade}
                    </div>
                  </div>
                </div>
                <div className="flex gap-1 sm:gap-2 flex-wrap">
                  {advData.advertiser?.kycVerified && (
                    <span className="bg-green-900/50 text-green-200 border border-green-700/50 px-2 py-1 rounded text-xs sm:text-sm flex items-center gap-1 font-medium">
                      <FaShieldAlt className="w-3 h-3" /> KYC
                    </span>
                  )}
                  {advData.advertiser?.badges?.map((badge, index) => (
                    <span
                      key={index}
                      className="bg-purple-900/50 text-purple-200 border border-purple-700/50 px-2 py-1 rounded text-xs sm:text-sm font-medium"
                    >
                      {badge}
                    </span>
                  ))}
                </div>
              </div>

              {/* Merchant Stats Grid */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                <div className="flex flex-col gap-1 p-3 bg-gray-800/50 rounded-lg">
                  <span className="text-gray-400 text-xs sm:text-sm font-medium">
                    BTC 30 ngày
                  </span>
                  <span className="text-green-400 font-bold text-sm sm:text-base">
                    {advData.advertiser?.userStatsRet?.completedOrderTotalBtcAmountOfLatest30day?.toFixed(6) || '0.000000'}
                  </span>
                </div>

                <div className="flex flex-col gap-1 p-3 bg-gray-800/50 rounded-lg">
                  <span className="text-gray-400 text-xs sm:text-sm font-medium">Tổng BTC</span>
                  <span className="text-blue-400 font-bold text-sm sm:text-base">
                    {advData.advertiser?.userStatsRet?.completedOrderTotalBtcAmount?.toFixed(6) || '0.000000'}
                  </span>
                </div>

                <div className="flex flex-col gap-1 p-3 bg-gray-800/50 rounded-lg">
                  <span className="text-gray-400 text-xs sm:text-sm font-medium">
                    BTC mua
                  </span>
                  <span className="text-yellow-400 font-bold text-sm sm:text-base">
                    {advData.advertiser?.userStatsRet?.completedBuyOrderTotalBtcAmount?.toFixed(6) || '0.000000'}
                  </span>
                </div>

                <div className="flex flex-col gap-1 p-3 bg-gray-800/50 rounded-lg">
                  <span className="text-gray-400 text-xs sm:text-sm font-medium">BTC bán</span>
                  <span className="text-purple-400 font-bold text-sm sm:text-base break-words">
                    {advData.advertiser?.userStatsRet?.completedSellOrderTotalBtcAmount?.toFixed(6) || '0.000000'}
                  </span>
                </div>
              </div>
              
              {/* Additional Merchant Info Row */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mt-3">
                <div className="flex flex-col gap-1 p-3 bg-gray-800/50 rounded-lg">
                  <span className="text-gray-400 text-xs sm:text-sm font-medium">
                    Đăng ký
                  </span>
                  <span className="text-cyan-400 font-bold text-sm sm:text-base">
                    {advData.advertiser?.userStatsRet?.registerDays || 0} ngày
                  </span>
                </div>

                <div className="flex flex-col gap-1 p-3 bg-gray-800/50 rounded-lg">
                  <span className="text-gray-400 text-xs sm:text-sm font-medium">Lệnh đầu</span>
                  <span className="text-orange-400 font-bold text-sm sm:text-base">
                    {advData.advertiser?.userStatsRet?.firstOrderDays || 0} ngày
                  </span>
                </div>

                <div className="flex flex-col gap-1 p-3 bg-gray-800/50 rounded-lg">
                  <span className="text-gray-400 text-xs sm:text-sm font-medium">
                    Xác nhận
                  </span>
                  <span className="text-yellow-400 font-bold text-sm sm:text-base">
                    {advData.advertiser?.advConfirmTime || 0}s
                  </span>
                </div>

                <div className="flex flex-col gap-1 p-3 bg-gray-800/50 rounded-lg">
                  <span className="text-gray-400 text-xs sm:text-sm font-medium">Ký quỹ</span>
                  <span className="text-purple-400 font-bold text-sm sm:text-base break-words">
                    {advData.advertiser?.margin} {advData.advertiser?.marginUnit}
                  </span>
                  <span className="text-gray-500 text-xs">
                    EQ: {advData.advertiser?.eqMargin} {advData.advertiser?.eqMarginUnit}
                  </span>
                </div>
              </div>
            </section>

            {/* Payment Methods - Collapsible */}
            {advData.adv?.tradeMethods &&
              advData.adv.tradeMethods.length > 0 && (
                <details className="group">
                  <summary className="p-3 sm:p-4 bg-gray-700/30 rounded-lg border border-gray-700/50 cursor-pointer hover:bg-gray-700/50 transition-colors">
                    <h3 className="text-white font-medium text-sm sm:text-base inline">
                      Phương thức thanh toán
                    </h3>
                    <span className="float-right text-gray-400 group-open:rotate-180 transition-transform">▼</span>
                  </summary>
                  <div className="mt-2 p-3 sm:p-4 bg-gray-700/30 rounded-lg border border-gray-700/50">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {advData.adv.tradeMethods.map((method, index) => (
                        <div
                          key={index}
                          className="p-3 bg-gray-700/50 border border-gray-600/50 rounded-lg hover:bg-gray-700/70 transition-colors"
                        >
                          <div className="flex items-center gap-3 mb-2">
                            {method.iconUrl && (
                              <img 
                                src={method.iconUrl} 
                                alt={method.tradeMethodName}
                                className="w-6 h-6 rounded"
                                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                              />
                            )}
                            <div>
                              <div className="text-gray-200 font-medium text-sm">
                                {method.tradeMethodName}
                              </div>
                              {method.tradeMethodShortName && method.tradeMethodShortName !== method.tradeMethodName && (
                                <div className="text-gray-400 text-xs">
                                  {method.tradeMethodShortName}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="text-xs text-gray-400 space-y-1">
                            <div><strong>Loại:</strong> {method.payType}</div>
                            <div><strong>ID:</strong> {method.identifier}</div>
                            {method.payBank && <div><strong>Ngân hàng:</strong> {method.payBank}</div>}
                            {method.paySubBank && <div><strong>Chi nhánh:</strong> {method.paySubBank}</div>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </details>
              )}

            {/* Remarks and Auto Reply */}
            {(advData.adv?.remarks || advData.adv?.autoReplyMsg) && (
              <div className="space-y-3">
                {advData.adv?.remarks && (
                  <div className="p-3 sm:p-4 bg-gray-700/30 rounded-lg border border-gray-700/50">
                    <h3 className="text-white font-medium mb-2 text-sm sm:text-base">Ghi chú</h3>
                    <div 
                      className="text-xs sm:text-sm text-gray-300 whitespace-pre-line break-words max-h-32 overflow-y-auto"
                      dangerouslySetInnerHTML={{ __html: advData.adv.remarks }}
                    />
                  </div>
                )}
                
                {advData.adv?.autoReplyMsg && (
                  <div className="p-3 sm:p-4 bg-blue-900/20 rounded-lg border border-blue-700/50">
                    <h3 className="text-white font-medium mb-2 text-sm sm:text-base">Tin nhắn tự động</h3>
                    <div 
                      className="text-xs sm:text-sm text-blue-200 whitespace-pre-line break-words"
                      dangerouslySetInnerHTML={{ __html: advData.adv.autoReplyMsg }}
                    />
                  </div>
                )}
              </div>
            )}

            {/* Additional Info - Expanded by default */}
            <details className="group" open>
              <summary className="p-3 sm:p-4 bg-gray-700/30 rounded-lg border border-gray-700/50 cursor-pointer hover:bg-gray-700/50 transition-colors">
                <h3 className="text-white font-medium text-sm sm:text-base inline">
                  Thông tin bổ sung
                </h3>
                <span className="float-right text-gray-400 group-open:rotate-180 transition-transform">▼</span>
              </summary>
              <div className="mt-2 p-3 sm:p-4 bg-gray-700/30 rounded-lg border border-gray-700/50">
                <div className="text-xs sm:text-sm text-gray-300 space-y-2 break-words">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <h4 className="font-medium text-white mb-2">Thông tin cơ bản</h4>
                      <p className="flex items-start gap-2">
                        <FaInfoCircle className="mt-0.5 flex-shrink-0" /> 
                        <span><strong>Trạng thái:</strong> {advData.adv?.advStatus}</span>
                      </p>
                      <p className="flex items-start gap-2">
                        <FaInfoCircle className="mt-0.5 flex-shrink-0" /> 
                        <span><strong>Loại giá:</strong> {advData.adv?.priceType}</span>
                      </p>
                      <p className="flex items-start gap-2">
                        <FaInfoCircle className="mt-0.5 flex-shrink-0" /> 
                        <span><strong>Tỷ lệ giá nổi:</strong> {advData.adv?.priceFloatingRatio}</span>
                      </p>
                      <p className="flex items-start gap-2">
                        <FaInfoCircle className="mt-0.5 flex-shrink-0" /> 
                        <span><strong>Tỷ lệ tỷ giá nổi:</strong> {advData.adv?.rateFloatingRatio}</span>
                      </p>
                      <p className="flex items-start gap-2">
                        <FaInfoCircle className="mt-0.5 flex-shrink-0" /> 
                        <span><strong>Số lượng ban đầu:</strong> {formatMoney(advData.adv?.initAmount || "0")} {advData.adv?.asset}</span>
                      </p>
                      <p className="flex items-start gap-2">
                        <FaInfoCircle className="mt-0.5 flex-shrink-0" /> 
                        <span><strong>Loại kho:</strong> {advData.adv?.inventoryType}</span>
                      </p>
                    </div>
                    
                    <div className="space-y-2">
                      <h4 className="font-medium text-white mb-2">Điều kiện giao dịch</h4>
                      <p className="flex items-start gap-2">
                        <FaInfoCircle className={`mt-0.5 flex-shrink-0 ${
                          Number(advData.adv?.buyerBtcPositionLimit) > 0 ? "text-red-400" : "text-green-400"
                        }`} /> 
                        <span><strong>Giới hạn BTC:</strong> {advData.adv?.buyerBtcPositionLimit}</span>
                      </p>
                      <p className="flex items-start gap-2">
                        <FaInfoCircle className="mt-0.5 flex-shrink-0" /> 
                        <span><strong>Ngày đăng ký tối thiểu:</strong> {advData.adv?.buyerRegDaysLimit} ngày</span>
                      </p>
                      <p className="flex items-start gap-2">
                        <FaInfoCircle className="mt-0.5 flex-shrink-0" /> 
                        <span><strong>KYC yêu cầu:</strong> {advData.adv?.buyerKycLimit}</span>
                      </p>
                      <p className="flex items-start gap-2">
                        <FaInfoCircle className="mt-0.5 flex-shrink-0" /> 
                        <span><strong>KYC bổ sung:</strong> {advData.adv?.takerAdditionalKycRequired ? 'Có' : 'Không'}</span>
                      </p>
                      <p className="flex items-start gap-2">
                        <FaInfoCircle className="mt-0.5 flex-shrink-0" /> 
                        <span><strong>Cho phép thương gia:</strong> {advData.adv?.allowTradeMerchant ? 'Có' : 'Không'}</span>
                      </p>
                      <p className="flex items-start gap-2">
                        <FaInfoCircle className="mt-0.5 flex-shrink-0" /> 
                        <span><strong>Thanh toán an toàn:</strong> {advData.adv?.isSafePayment ? 'Có' : 'Không'}</span>
                      </p>
                    </div>
                  </div>
                  
                  <div className="mt-4 pt-4 border-t border-gray-600">
                    <h4 className="font-medium text-white mb-2">Thời gian</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <p className="flex items-start gap-2">
                        <FaInfoCircle className="mt-0.5 flex-shrink-0" /> 
                        <span><strong>Thời gian tạo:</strong> {processedAdvData?.formattedCreateTime}</span>
                      </p>
                      <p className="flex items-start gap-2">
                        <FaInfoCircle className="mt-0.5 flex-shrink-0" /> 
                        <span><strong>Cập nhật:</strong> {processedAdvData?.formattedUpdateTime}</span>
                      </p>
                    </div>
                  </div>
                  
                  {(advData.adv?.abnormalStatusList && advData.adv.abnormalStatusList.length > 0) && (
                    <div className="mt-4 pt-4 border-t border-gray-600">
                      <h4 className="font-medium text-white mb-2">Trạng thái bất thường</h4>
                      <div className="flex flex-wrap gap-1">
                        {advData.adv.abnormalStatusList.map((status, index) => (
                          <span key={index} className="px-2 py-1 bg-red-900/50 text-red-200 border border-red-700/50 rounded text-xs">
                            {status}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </details>

            {/* Trading Constraints - Collapsible */}
            <details className="group">
              <summary className="p-3 sm:p-4 bg-gray-700/30 rounded-lg border border-gray-700/50 cursor-pointer hover:bg-gray-700/50 transition-colors">
                <h3 className="text-white font-medium text-sm sm:text-base inline">
                  Điều kiện giao dịch chi tiết
                </h3>
                <span className="float-right text-gray-400 group-open:rotate-180 transition-transform">▼</span>
              </summary>
              <div className="mt-2 p-3 sm:p-4 bg-gray-700/30 rounded-lg border border-gray-700/50">
                <div className="text-xs sm:text-sm text-gray-300 space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <h4 className="font-medium text-white mb-2">Giới hạn số lượng giao dịch</h4>
                      <p><strong>Thời gian lọc:</strong> {advData.adv?.userTradeCountFilterTime}</p>
                      <p><strong>Số lệnh mua (min-max):</strong> {advData.adv?.userBuyTradeCountMin} - {advData.adv?.userBuyTradeCountMax}</p>
                      <p><strong>Số lệnh bán (min-max):</strong> {advData.adv?.userSellTradeCountMin} - {advData.adv?.userSellTradeCountMax}</p>
                      <p><strong>Tổng số lệnh (min-max):</strong> {advData.adv?.userAllTradeCountMin} - {advData.adv?.userAllTradeCountMax}</p>
                    </div>
                    
                    <div className="space-y-2">
                      <h4 className="font-medium text-white mb-2">Giới hạn khối lượng</h4>
                      <p><strong>Thời gian lọc:</strong> {advData.adv?.userTradeVolumeFilterTime}</p>
                      <p><strong>Loại giao dịch:</strong> {advData.adv?.userTradeType}</p>
                      <p><strong>Khối lượng (min-max):</strong> {advData.adv?.userTradeVolumeMin} - {advData.adv?.userTradeVolumeMax}</p>
                      {advData.adv?.userTradeVolumeAsset && (
                        <p><strong>Tài sản khối lượng:</strong> {advData.adv.userTradeVolumeAsset}</p>
                      )}
                    </div>
                  </div>
                  
                  <div className="pt-4 border-t border-gray-600">
                    <h4 className="font-medium text-white mb-2">Tỷ lệ hoàn thành</h4>
                    <div className="space-y-1">
                      {advData.adv?.userTradeCompleteRateFilterTime && (
                        <p><strong>Thời gian lọc:</strong> {advData.adv.userTradeCompleteRateFilterTime}</p>
                      )}
                      <p><strong>Số lượng hoàn thành tối thiểu:</strong> {advData.adv?.userTradeCompleteCountMin}</p>
                      <p><strong>Tỷ lệ hoàn thành tối thiểu:</strong> {formatPercent(advData.adv?.userTradeCompleteRateMin || "0")}</p>
                    </div>
                  </div>
                </div>
              </div>
            </details>

            {/* Commission Rates - Collapsible */}
            {advData.adv?.tradeMethodCommissionRates &&
              advData.adv.tradeMethodCommissionRates.length > 0 && (
                <details className="group">
                  <summary className="p-3 sm:p-4 bg-gray-700/30 rounded-lg border border-gray-700/50 cursor-pointer hover:bg-gray-700/50 transition-colors">
                    <h3 className="text-white font-medium text-sm sm:text-base inline">
                      Tỷ lệ hoa hồng theo phương thức
                    </h3>
                    <span className="float-right text-gray-400 group-open:rotate-180 transition-transform">▼</span>
                  </summary>
                  <div className="mt-2 p-3 sm:p-4 bg-gray-700/30 rounded-lg border border-gray-700/50">
                    <div className="text-xs sm:text-sm text-gray-300 space-y-2">
                      {advData.adv.tradeMethodCommissionRates.map((rate, index) => (
                        <p key={index} className="flex items-center gap-2 break-words">
                          <FaPercent className="flex-shrink-0" />
                          <span><strong>{rate.tradeMethodName}:</strong> {formatPercent(rate.commissionRate)}</span>
                        </p>
                      ))}
                      {advData.adv?.takerCommissionRate && (
                        <p className="flex items-center gap-2 break-words">
                          <FaPercent className="flex-shrink-0" />
                          <span><strong>Hoa hồng taker:</strong> {formatPercent(advData.adv.takerCommissionRate)}</span>
                        </p>
                      )}
                      {advData.adv?.minTakerFee && (
                        <p className="flex items-center gap-2 break-words">
                          <FaPercent className="flex-shrink-0" />
                          <span><strong>Phí taker tối thiểu:</strong> {advData.adv.minTakerFee}</span>
                        </p>
                      )}
                    </div>
                  </div>
                </details>
              )}

            {/* User Stats - Expanded by default */}
            {advData.advertiser?.userStatsRet && (
              <details className="group" open>
                <summary className="p-3 sm:p-4 bg-gray-700/30 rounded-lg border border-gray-700/50 cursor-pointer hover:bg-gray-700/50 transition-colors">
                  <h3 className="text-white font-medium text-sm sm:text-base inline">
                    Thống kê chi tiết
                  </h3>
                  <span className="float-right text-gray-400 group-open:rotate-180 transition-transform">▼</span>
                </summary>
                <div className="mt-2 p-3 sm:p-4 bg-gray-700/30 rounded-lg border border-gray-700/50">
                  <div className="text-xs sm:text-sm text-gray-300 space-y-2 break-words">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <h4 className="font-medium text-white mb-2">Thông tin cơ bản</h4>
                        <p className="flex items-start gap-2">
                          <FaClock className="mt-0.5 flex-shrink-0" />
                          <span><strong>Đăng ký:</strong> {advData.advertiser.userStatsRet.registerDays} ngày</span>
                        </p>
                        <p className="flex items-start gap-2">
                          <FaClock className="mt-0.5 flex-shrink-0" />
                          <span><strong>Lệnh đầu:</strong> {advData.advertiser.userStatsRet.firstOrderDays} ngày</span>
                        </p>
                        <p className="flex items-start gap-2">
                          <FaUserCircle className="mt-0.5 flex-shrink-0" />
                          <span><strong>Đối tác:</strong> {advData.advertiser.userStatsRet.counterpartyCount}</span>
                        </p>
                        <p className="flex items-start gap-2">
                          <FaInfoCircle className="mt-0.5 flex-shrink-0" />
                          <span><strong>Kênh liên quan:</strong> {advData.advertiser.chanelInvolved}</span>
                        </p>
                        <p className="flex items-start gap-2">
                          <FaInfoCircle className="mt-0.5 flex-shrink-0" />
                          <span><strong>Thang điểm:</strong> {advData.advertiser.decimalScale}</span>
                        </p>
                      </div>
                      
                      <div className="space-y-2">
                        <h4 className="font-medium text-white mb-2">Hiệu suất 30 ngày</h4>
                        <p className="flex items-start gap-2">
                          <FaPercent className="mt-0.5 flex-shrink-0 text-green-400" />
                          <span><strong>Tỷ lệ hoàn thành:</strong> {formatPercent(advData.advertiser.userStatsRet.finishRateLatest30day.toString())}</span>
                        </p>
                        <p className="flex items-start gap-2">
                          <FaChartLine className="mt-0.5 flex-shrink-0" />
                          <span><strong>Lệnh hoàn thành:</strong> {advData.advertiser.userStatsRet.completedOrderNumOfLatest30day}</span>
                        </p>
                        <p className="flex items-start gap-2">
                          <FaChartLine className="mt-0.5 flex-shrink-0" />
                          <span><strong>Lệnh mua 30 ngày:</strong> {advData.advertiser.userStatsRet.completedBuyOrderNumOfLatest30day}</span>
                        </p>
                        <p className="flex items-start gap-2">
                          <FaChartLine className="mt-0.5 flex-shrink-0" />
                          <span><strong>Lệnh bán 30 ngày:</strong> {advData.advertiser.userStatsRet.completedSellOrderNumOfLatest30day}</span>
                        </p>
                        <p className="flex items-start gap-2">
                          <FaHourglassHalf className="mt-0.5 flex-shrink-0" />
                          <span><strong>Thời gian giải phóng:</strong> {advData.advertiser.userStatsRet.avgReleaseTimeOfLatest30day.toFixed(1)}s</span>
                        </p>
                        <p className="flex items-start gap-2">
                          <FaHourglassHalf className="mt-0.5 flex-shrink-0" />
                          <span><strong>Thời gian thanh toán:</strong> {advData.advertiser.userStatsRet.avgPayTimeOfLatest30day.toFixed(1)}s</span>
                        </p>
                      </div>
                    </div>
                    
                    <div className="mt-4 pt-4 border-t border-gray-600">
                      <h4 className="font-medium text-white mb-2">Tổng kết toàn thời gian</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
                        <p><strong>Tổng lệnh:</strong> {advData.advertiser.userStatsRet.completedOrderNum}</p>
                        <p><strong>Lệnh mua:</strong> {advData.advertiser.userStatsRet.completedBuyOrderNum}</p>
                        <p><strong>Lệnh bán:</strong> {advData.advertiser.userStatsRet.completedSellOrderNum}</p>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs mt-2">
                        <p><strong>Tổng BTC mua:</strong> {advData.advertiser.userStatsRet.completedBuyOrderTotalBtcAmount.toFixed(6)}</p>
                        <p><strong>Tổng BTC bán:</strong> {advData.advertiser.userStatsRet.completedSellOrderTotalBtcAmount.toFixed(6)}</p>
                        <p><strong>Tổng BTC:</strong> {advData.advertiser.userStatsRet.completedOrderTotalBtcAmount.toFixed(6)}</p>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs mt-2">
                        <p><strong>BTC 30 ngày:</strong> {advData.advertiser.userStatsRet.completedOrderTotalBtcAmountOfLatest30day.toFixed(6)}</p>
                        <p><strong>Tên công ty:</strong> {advData.advertiser.companyAccountName || 'N/A'}</p>
                      </div>
                    </div>
                    
                    {advData.advertiser.userKycRet && (
                      <div className="mt-4 pt-4 border-t border-gray-600">
                        <h4 className="font-medium text-white mb-2">Thông tin KYC</h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                          <p><strong>Loại KYC:</strong> {advData.advertiser.userKycRet.kycType}</p>
                          <p><strong>Trạng thái địa chỉ:</strong> {advData.advertiser.userKycRet.addressStatus}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </details>
            )}
          </article>
        )}
      </div>
    </main>
  );
};

const MemoizedAdvDetail = memo(AdvDetail);

export default MemoizedAdvDetail;